import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions, AccessLevel, ModuleKey } from "@/hooks/useOrgPermissions";
import { toast } from "@/hooks/use-toast";
import { Loader2, Network, Shield, Users, Grid3x3, Lock, List, LayoutTemplate, Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";

const MODULES: { key: ModuleKey; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "media_registry", label: "Media Registry" },
  { key: "accreditation", label: "Accreditation" },
  { key: "events", label: "Events" },
  { key: "coverage_analytics", label: "Coverage Analytics" },
  { key: "awards", label: "Awards" },
  { key: "social_media", label: "Social Media" },
  { key: "organogram", label: "Organogram" },
  { key: "media_kits", label: "Media Kits" },
  { key: "journalist_portal", label: "Journalist Portal" },
];

const ACCESS_BADGE: Record<AccessLevel, string> = {
  full: "bg-emerald-600 text-white",
  edit: "bg-amber-500 text-white",
  view: "bg-blue-500 text-white",
  none: "bg-muted text-muted-foreground",
};

export default function Organization() {
  const ctx = useOrgPermissions();

  if (ctx.loading) return <ConorbitLoader variant="page" />;

  if (!ctx.orgId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You are not a member of any organisation.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Organisation</h1>
        <p className="text-justify text-muted-foreground">
          Manage organogram, roles, permissions and members. Your role:{" "}
          <span className="font-semibold text-foreground">{ctx.roleName ?? ""}</span>
        </p>
      </div>

      <Tabs defaultValue="organogram" className="space-y-6">
        <TabsList>
          <TabsTrigger value="organogram"><Network className="w-4 h-4 mr-2" />Organogram</TabsTrigger>
          <TabsTrigger value="roles"><Shield className="w-4 h-4 mr-2" />Roles</TabsTrigger>
          <TabsTrigger value="matrix"><Grid3x3 className="w-4 h-4 mr-2" />Permissions Matrix</TabsTrigger>
          <TabsTrigger value="members"><Users className="w-4 h-4 mr-2" />Members</TabsTrigger>
        </TabsList>

        <TabsContent value="organogram"><OrganogramTab orgId={ctx.orgId} canEdit={ctx.can("organogram","edit")} /></TabsContent>
        <TabsContent value="roles"><RolesTab orgId={ctx.orgId} canManage={ctx.can("organogram","full")} /></TabsContent>
        <TabsContent value="matrix"><PermissionsMatrixTab orgId={ctx.orgId} canManage={ctx.can("organogram","full")} onChange={ctx.refresh} /></TabsContent>
        <TabsContent value="members"><MembersTab orgId={ctx.orgId} canManage={ctx.can("organogram","full")} /></TabsContent>
      </Tabs>
    </div>
  );
}

// Depth-based colour palette for organogram nodes
const DEPTH_COLORS = [
  { bg: '#0D2240', text: '#F0E4B8', border: '#C8A84B' }, // depth 0 - navy/gold
  { bg: '#006B3C', text: '#ffffff', border: '#00A65A' }, // depth 1 - green
  { bg: '#1E4E8C', text: '#ffffff', border: '#3B82F6' }, // depth 2 - blue
  { bg: '#7C3AED', text: '#ffffff', border: '#A78BFA' }, // depth 3 - violet
  { bg: '#C2410C', text: '#ffffff', border: '#FB923C' }, // depth 4 - orange
  { bg: '#0F766E', text: '#ffffff', border: '#2DD4BF' }, // depth 5+ - teal
];
const depthColor = (depth: number) => DEPTH_COLORS[Math.min(depth, DEPTH_COLORS.length - 1)];

const PERM_LEVELS = ['none', 'view', 'edit', 'full'] as const
const PERM_COLORS: Record<string, string> = {
  full: 'bg-emerald-600 text-white',
  edit: 'bg-amber-500 text-white',
  view: 'bg-blue-500 text-white',
  none: 'bg-muted text-muted-foreground',
}
const ORG_MODULES = [
  { key: 'dashboard',          label: 'Dashboard' },
  { key: 'media_registry',     label: 'Media Registry' },
  { key: 'accreditation',      label: 'Accreditation' },
  { key: 'events',             label: 'Events' },
  { key: 'coverage_analytics', label: 'Coverage Analytics' },
  { key: 'awards',             label: 'Awards' },
  { key: 'social_media',       label: 'Social Media' },
  { key: 'organogram',         label: 'Organogram' },
  { key: 'media_kits',         label: 'Media Kits' },
  { key: 'journalist_portal',  label: 'Journalist Portal' },
  { key: 'press_releases',     label: 'Press Releases' },
]

// ---------- Organogram ----------
function OrganogramTab({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const [units, setUnits]         = useState<any[]>([]);
  const [viewMode, setViewMode]   = useState<'tree' | 'table'>('tree');
  const [name, setName]           = useState("");
  const [unitType, setUnitType]   = useState("unit");
  const [parentId, setParentId]   = useState<string>("root");
  const [unitColor, setUnitColor] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName]   = useState("");
  const [editColor, setEditColor]   = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  // Permission sheet state
  const [permUnit,    setPermUnit]    = useState<any | null>(null);
  const [perms,       setPerms]       = useState<Record<string, string>>({});
  const [savingPerms, setSavingPerms] = useState(false);

  // Custom permission ticket state
  const [ticketOpen,   setTicketOpen]   = useState(false);
  const [ticketModule, setTicketModule] = useState('');
  const [ticketLevel,  setTicketLevel]  = useState('');
  const [ticketReason, setTicketReason] = useState('');
  const [submittingTicket, setSubmittingTicket] = useState(false);

  const load = async () => {
    const { data: u } = await (supabase.from("org_units" as any).select("*").eq("org_id", orgId).order("display_order") as any);
    setUnits((u as any[]) ?? []);
  };
  useEffect(() => { load(); }, [orgId]);

  const addUnit = async () => {
    if (!name.trim()) return;
    // role_id is NOT sent — the DB trigger auto-creates and links a role
    const { error } = await (supabase.from("org_units" as any).insert({
      org_id: orgId, name: name.trim(),
      parent_unit_id: parentId === "root" ? null : parentId,
      type: unitType,
      color: unitColor || null,
    }) as any);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { setName(""); setUnitType("unit"); setUnitColor(""); setShowAddForm(false); load(); }
  };

  const saveEdit = async (id: string) => {
    const { error } = await (supabase.from("org_units" as any)
      .update({ name: editName.trim(), color: editColor || null })
      .eq("id", id) as any);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { setEditingId(null); load(); }
  };

  const deleteUnit = async (id: string) => {
    if (!window.confirm("Remove this position and its auto-linked role?")) return;
    const { error } = await (supabase.from("org_units" as any).delete().eq("id", id) as any);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else load();
  };

  const startEdit = (u: any) => {
    setEditingId(u.id); setEditName(u.name); setEditColor(u.color ?? "");
  };

  // ── Open permission editor for a unit ────────────────────────────────────
  const openPerms = async (u: any) => {
    setPermUnit(u);
    if (!u.role_id) { setPerms({}); return; }
    const { data } = await (supabase.from("role_permissions" as any)
      .select("module, access_level")
      .eq("role_id", u.role_id) as any);
    const map: Record<string, string> = {};
    ORG_MODULES.forEach(m => { map[m.key] = 'none'; });
    (data ?? []).forEach((p: any) => { map[p.module] = p.access_level; });
    setPerms(map);
  };

  const savePerms = async () => {
    if (!permUnit?.role_id) return;
    setSavingPerms(true);
    try {
      await Promise.all(ORG_MODULES.map(m =>
        (supabase.from("role_permissions" as any).upsert(
          { role_id: permUnit.role_id, module: m.key, access_level: perms[m.key] ?? 'none' },
          { onConflict: 'role_id,module' }
        ) as any)
      ));
      toast({ title: "Permissions saved for " + permUnit.name });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingPerms(false);
    }
  };

  const submitTicket = async () => {
    if (!ticketModule || !ticketLevel || !ticketReason.trim()) return;
    setSubmittingTicket(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await (supabase.from("support_tickets" as any).insert({
        org_id:       orgId,
        submitted_by: user.id,
        title:        `Custom Permission Request: ${permUnit?.name} — ${ORG_MODULES.find(m => m.key === ticketModule)?.label}`,
        description:  `Position: ${permUnit?.name}\nRequested module: ${ticketModule}\nRequested access level: ${ticketLevel}\n\nJustification:\n${ticketReason.trim()}`,
        priority:     'medium',
        status:       'open',
      }) as any);
      if (error) throw error;
      toast({ title: "Ticket submitted", description: "Your custom permission request has been sent." });
      setTicketOpen(false); setTicketModule(''); setTicketLevel(''); setTicketReason('');
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally {
      setSubmittingTicket(false);
    }
  };

  const childrenOf = (id: string | null) => units.filter(u => u.parent_unit_id === id);

  // ── Visual tree node
  const renderTreeNode = (u: any, depth = 0) => {
    const kids = childrenOf(u.id);
    const col = u.color ? { bg: u.color, text: '#ffffff', border: u.color } : depthColor(depth);
    const isEditing = editingId === u.id;

    return (
      <div key={u.id} className="flex flex-col items-center">
        {depth > 0 && <div className="w-px h-6 bg-border" />}

        <div
          className="rounded-xl px-4 py-3 min-w-[140px] max-w-[200px] text-center shadow-md border-2 relative cursor-pointer"
          style={{ backgroundColor: col.bg, color: col.text, borderColor: col.border }}
          onClick={() => !isEditing && openPerms(u)}
        >
          {isEditing ? (
            <div className="space-y-1" onClick={e => e.stopPropagation()}>
              <input
                className="w-full text-xs rounded px-1 py-0.5 text-black"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                autoFocus
              />
              <input
                type="color"
                className="w-full h-5 rounded cursor-pointer"
                value={editColor || col.bg}
                onChange={e => setEditColor(e.target.value)}
              />
              <div className="flex justify-center gap-1 mt-1">
                <button onClick={() => saveEdit(u.id)} className="p-1 rounded bg-white/20 hover:bg-white/30"><Check className="w-3 h-3" /></button>
                <button onClick={() => setEditingId(null)} className="p-1 rounded bg-white/20 hover:bg-white/30"><X className="w-3 h-3" /></button>
              </div>
            </div>
          ) : (
            <>
              <div className="font-semibold text-sm leading-tight">{u.name}</div>
              <div className="text-[9px] mt-1 opacity-60 capitalize">{u.type.replace(/_/g, " ")}</div>
              <div className="text-[9px] mt-0.5 opacity-40 flex items-center justify-center gap-1">
                <Lock className="w-2.5 h-2.5" /> Click to set permissions
              </div>
              {canEdit && (
                <div className="absolute -top-2 -right-2 flex gap-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => startEdit(u)} className="w-5 h-5 rounded-full bg-white/90 flex items-center justify-center shadow">
                    <Pencil className="w-2.5 h-2.5 text-gray-700" />
                  </button>
                  <button onClick={() => deleteUnit(u.id)} className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shadow">
                    <Trash2 className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* children */}
        {kids.length > 0 && (
          <div className="flex flex-col items-center w-full">
            <div className="w-px h-4 bg-border" />
            {/* horizontal bar */}
            {kids.length > 1 && (
              <div className="relative flex justify-center w-full">
                <div
                  className="absolute top-0 h-px bg-border"
                  style={{ left: `calc(${100 / (kids.length * 2)}% )`, right: `calc(${100 / (kids.length * 2)}% )` }}
                />
              </div>
            )}
            <div className="flex items-start gap-6 mt-0">
              {kids.map(c => renderTreeNode(c, depth + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const roots = childrenOf(null);

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Organogram</CardTitle>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('tree')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'tree' ? 'bg-[#0D2240] text-white' : 'text-muted-foreground hover:bg-muted'}`}
              >
                <LayoutTemplate className="w-3.5 h-3.5" /> Tree
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-[#0D2240] text-white' : 'text-muted-foreground hover:bg-muted'}`}
              >
                <List className="w-3.5 h-3.5" /> Table
              </button>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setShowAddForm(v => !v)} className="gap-1 h-8">
                <Plus className="w-3.5 h-3.5" /> Add Position
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Add form */}
        {showAddForm && canEdit && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 border border-dashed border-border rounded-xl bg-muted/30">
            <Input placeholder="Position / Unit name" value={name} onChange={e => setName(e.target.value)} />
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger><SelectValue placeholder="Parent unit" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="root">Root (top level)</SelectItem>
                {units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={unitType} onValueChange={setUnitType}>
              <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unit">Unit</SelectItem>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="head_of_comms">Head of Comms</SelectItem>
                <SelectItem value="speaker">Speaker</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Colour</label>
              <input type="color" value={unitColor || '#0D2240'} onChange={e => setUnitColor(e.target.value)} className="h-8 w-12 rounded cursor-pointer border border-border" />
            </div>
            <div className="col-span-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              A role is automatically created and linked to this position. You can set its permissions after adding.
            </div>
            <div className="flex gap-2">
              <Button onClick={addUnit} className="flex-1 h-8 text-xs">Add Position</Button>
              <Button variant="ghost" onClick={() => setShowAddForm(false)} className="h-8 text-xs">Cancel</Button>
            </div>
          </div>
        )}

        {/* Colour legend */}
        {viewMode === 'tree' && units.length > 0 && (
          <div className="flex flex-wrap gap-2 text-[11px]">
            {DEPTH_COLORS.slice(0, 4).map((c, i) => (
              <span key={i} className="flex items-center gap-1.5 px-2 py-0.5 rounded-full" style={{ backgroundColor: c.bg, color: c.text }}>
                Level {i + 1}
              </span>
            ))}
            <span className="text-muted-foreground ml-1">Custom colours override level colours.</span>
          </div>
        )}

        {/* Tree view */}
        {viewMode === 'tree' && (
          <div className="overflow-x-auto py-4">
            {roots.length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground text-center py-8">
                No positions yet. Click "Add Position" to build your organogram.
              </p>
            ) : (
              <div className="flex gap-8 justify-start items-start min-w-max pb-4">
                {roots.map(r => renderTreeNode(r, 0))}
              </div>
            )}
          </div>
        )}

        {/* Table view */}
        {viewMode === 'table' && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Position / Unit</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead className="text-right">Permissions</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">No positions yet.</TableCell>
                </TableRow>
              )}
              {units.map((u, idx) => {
                const parent = units.find(p => p.id === u.parent_unit_id);
                const col = u.color ?? depthColor(0).bg;
                return (
                  <TableRow key={u.id} className={idx % 2 === 1 ? 'bg-muted/30' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: col }} />
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{u.type.replace(/_/g, ' ')}</TableCell>
                    <TableCell>{parent?.name ?? <span className="text-muted-foreground text-xs">Root</span>}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => openPerms(u)}>
                        <Shield className="w-3 h-3" /> Set Permissions
                      </Button>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => startEdit(u)}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => deleteUnit(u.id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>

    {/* ── Permissions sheet ─────────────────────────────────────────────── */}
    {permUnit && (
      <div className="fixed inset-0 z-50 flex">
        <div className="absolute inset-0 bg-black/40" onClick={() => setPermUnit(null)} />
        <div className="relative ml-auto w-full max-w-md bg-background border-l shadow-xl flex flex-col h-full overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-[#0D2240]">{permUnit.name}</h2>
                <p className="text-xs text-muted-foreground capitalize mt-0.5">{permUnit.type?.replace(/_/g, ' ')} · Auto-linked role</p>
              </div>
              <button onClick={() => setPermUnit(null)} className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="flex-1 p-6 space-y-1">
            <p className="text-xs text-muted-foreground mb-3">
              Set what this position can access. Each module can be set to <strong>none</strong>, <strong>view</strong>, <strong>edit</strong>, or <strong>full</strong>.
            </p>
            {ORG_MODULES.map((mod, i) => (
              <div key={mod.key} className={`flex items-center justify-between py-2 px-3 rounded-lg ${i % 2 === 1 ? 'bg-muted/40' : ''}`}>
                <span className="text-sm font-medium">{mod.label}</span>
                <div className="flex gap-1">
                  {PERM_LEVELS.map(lvl => (
                    <button
                      key={lvl}
                      onClick={() => setPerms(prev => ({ ...prev, [mod.key]: lvl }))}
                      className={[
                        'px-2 py-0.5 rounded text-[11px] font-medium border transition-colors',
                        perms[mod.key] === lvl
                          ? PERM_COLORS[lvl]
                          : 'bg-background border-border text-muted-foreground hover:border-zinc-400',
                      ].join(' ')}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="p-6 border-t space-y-3">
            <Button onClick={savePerms} disabled={savingPerms} className="w-full gap-1.5 bg-[#0D2240] hover:bg-[#1a3260] text-white">
              {savingPerms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              Save Permissions
            </Button>
            <button
              onClick={() => setTicketOpen(true)}
              className="w-full text-xs text-center text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Need a permission not listed above? Request it via ticket
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Custom permission ticket dialog ──────────────────────────────── */}
    {ticketOpen && permUnit && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={() => setTicketOpen(false)} />
        <div className="relative bg-background rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#0D2240]">Request Custom Permission</h3>
            <button onClick={() => setTicketOpen(false)}><X className="w-5 h-5 text-muted-foreground" /></button>
          </div>
          <p className="text-xs text-muted-foreground">
            For position <strong>{permUnit.name}</strong>. A support ticket will be submitted to the platform administrators.
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Module</label>
              <Select value={ticketModule} onValueChange={setTicketModule}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Which module?" /></SelectTrigger>
                <SelectContent>
                  {ORG_MODULES.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                  <SelectItem value="custom">Other / Custom module</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Required Access Level</label>
              <Select value={ticketLevel} onValueChange={setTicketLevel}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Access level needed" /></SelectTrigger>
                <SelectContent>
                  {PERM_LEVELS.filter(l => l !== 'none').map(l => (
                    <SelectItem key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Justification</label>
              <textarea
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0D2240]"
                rows={4}
                placeholder="Explain why this position needs this permission..."
                value={ticketReason}
                onChange={e => setTicketReason(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" onClick={() => setTicketOpen(false)} className="flex-1">Cancel</Button>
            <Button
              onClick={submitTicket}
              disabled={submittingTicket || !ticketModule || !ticketLevel || !ticketReason.trim()}
              className="flex-1 bg-[#0D2240] hover:bg-[#1a3260] text-white"
            >
              {submittingTicket ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Submit Ticket
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ---------- Roles ----------
function RolesTab({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const [roles, setRoles] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const load = async () => {
    // @ts-ignore
    const { data } = await supabase.from("roles" as any).select("*").eq("org_id", orgId).order("is_system", { ascending: false }).order("name");
    setRoles((data as any[]) ?? []);
  };
  useEffect(() => { load(); }, [orgId]);

  const addRole = async () => {
    if (!name.trim()) return;
    // @ts-ignore
    const { error } = await supabase.from("roles" as any).insert({ org_id: orgId, name, description, is_system: false });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { setName(""); setDescription(""); load(); }
  };

  const deleteRole = async (id: string) => {
    // @ts-ignore
    const { error } = await supabase.from("roles" as any).delete().eq("id", id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else load();
  };

  return (
    <Card>
      <CardHeader><CardTitle>Roles</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {canManage && (
          <div className="flex gap-2">
            <Input placeholder="Role name" value={name} onChange={e => setName(e.target.value)} />
            <Input placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
            <Button onClick={addRole}>Add Role</Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground">{r.description ?? ""}</TableCell>
                <TableCell>
                  {r.is_system ? (
                    <Badge variant="secondary"><Lock className="w-3 h-3 mr-1" />System</Badge>
                  ) : <Badge variant="outline">Custom</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {canManage && !r.is_system && (
                    <Button size="sm" variant="ghost" onClick={() => deleteRole(r.id)}>Delete</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------- Permissions Matrix ----------
function PermissionsMatrixTab({ orgId, canManage, onChange }: { orgId: string; canManage: boolean; onChange: () => void }) {
  const [roles, setRoles] = useState<any[]>([]);
  const [perms, setPerms] = useState<Record<string, Record<string, AccessLevel>>>({}); // role_id -> module -> level

  const load = async () => {
    // @ts-ignore
    const { data: r } = await supabase.from("roles" as any).select("*").eq("org_id", orgId).order("is_system", { ascending: false }).order("name");
    setRoles((r as any[]) ?? []);
    const roleIds = ((r as any[]) ?? []).map(x => x.id);
    if (roleIds.length === 0) return;
    // @ts-ignore
    const { data: p } = await supabase.from("role_permissions" as any).select("role_id, module, access_level").in("role_id", roleIds);
    const map: Record<string, Record<string, AccessLevel>> = {};
    (p as any[] | null)?.forEach(row => {
      map[row.role_id] = map[row.role_id] ?? {};
      map[row.role_id][row.module] = row.access_level;
    });
    setPerms(map);
  };
  useEffect(() => { load(); }, [orgId]);

  const setLevel = async (roleId: string, module: ModuleKey, level: AccessLevel) => {
    setPerms(prev => ({ ...prev, [roleId]: { ...(prev[roleId] ?? {}), [module]: level } }));
    // @ts-ignore
    const { error } = await supabase.from("role_permissions" as any)
      .upsert({ role_id: roleId, module, access_level: level }, { onConflict: "role_id,module" });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { onChange(); }
  };

  return (
    <Card>
      <CardHeader><CardTitle>Permissions Matrix</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2 font-medium text-muted-foreground">Module</th>
              {roles.map(r => (
                <th key={r.id} className="text-left p-2 font-medium">{r.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULES.map(m => (
              <tr key={m.key} className="border-b">
                <td className="p-2 font-medium">{m.label}</td>
                {roles.map(r => {
                  const level = perms[r.id]?.[m.key] ?? "none";
                  const isLocked = !canManage || r.name === "CCO"; // CCO permissions are locked, app admin only
                  return (
                    <td key={r.id} className="p-2">
                      {isLocked ? (
                        <Badge className={ACCESS_BADGE[level]}>{level}</Badge>
                      ) : (
                        <Select value={level} onValueChange={(v) => setLevel(r.id, m.key, v as AccessLevel)}>
                          <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">none</SelectItem>
                            <SelectItem value="view">view</SelectItem>
                            <SelectItem value="edit">edit</SelectItem>
                            <SelectItem value="full">full</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-justify text-xs text-muted-foreground mt-4">
          Institution Admin column is locked. Institution Admin always has full access.
        </p>
      </CardContent>
    </Card>
  );
}

// ---------- Members ----------
function MembersTab({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const [members, setMembers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState<string>("");
  const [emailStatus, setEmailStatus] = useState<any>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const { data: isAppAdmin } = useQuery({
    queryKey: ['is_app_admin'],
    queryFn: async () => {
      const { data } = await supabase.rpc('is_app_admin' as any);
      return data as boolean;
    }
  });

  const load = async () => {
    // @ts-ignore
    const { data: m } = await supabase
      .from("organization_members" as any)
      .select("id, user_id, status, joined_at, role_id, roles(name), profiles!organization_members_user_id_fkey(full_name)")
      .eq("org_id", orgId);
    // profiles join may not work via FK, fetch separately
    const list = (m as any[]) ?? [];
    if (list.length) {
      const ids = list.map(x => x.user_id);
      // @ts-ignore
      const { data: profs } = await supabase.from("profiles").select("user_id, full_name").in("user_id", ids);
      const pmap: Record<string, string> = {};
      (profs as any[] | null)?.forEach(p => { pmap[p.user_id] = p.full_name; });
      list.forEach(x => { x.full_name = pmap[x.user_id] ?? x.user_id.slice(0, 8); });
    }
    setMembers(list);
    // @ts-ignore
    const { data: r } = await supabase.from("roles" as any).select("id, name").eq("org_id", orgId).order("name");
    setRoles((r as any[]) ?? []);
  };
  useEffect(() => { load(); }, [orgId]);

  const changeRole = async (memberId: string, roleId: string) => {
    // @ts-ignore
    const { error } = await supabase.from("organization_members" as any).update({ role_id: roleId }).eq("id", memberId);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else load();
  };

  const setStatus = async (memberId: string, status: string) => {
    // @ts-ignore
    const { error } = await supabase.from("organization_members" as any).update({ status }).eq("id", memberId);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else load();
  };

  const checkEmail = async (email: string) => {
    if (!email || !email.includes('@')) return;
    setCheckingEmail(true);
    try {
      const { data } = await supabase.rpc('check_email_org_conflict' as any, {
        p_email: email,
        p_org_id: orgId,
      });
      setEmailStatus(data);
    } catch {
      setEmailStatus(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  // CCO assignment gate, only platform admins may assign the CCO role
  const availableRoles = roles.filter(
    (r: any) => isAppAdmin || r.name !== 'CCO'
  );

  const sendInvite = async () => {
    if (!inviteEmail || !inviteRoleId) {
      toast({ title: "Missing data", description: "Enter email and select a role." });
      return;
    }
    if (emailStatus?.status === 'conflict') {
      toast({ title: "Email conflict", description: "This email is registered to another institution.", variant: "destructive" });
      return;
    }
    const token = crypto.randomUUID();
    // @ts-ignore
    const { error } = await supabase.from("org_invitations" as any).insert({
      org_id: orgId, email: inviteEmail, role_id: inviteRoleId, token,
    });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Invitation created", description: `Token: ${token.slice(0, 12)}…` });
      setInviteEmail("");
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>Members</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {canManage && (
          <div className="flex gap-2 flex-wrap items-start">
            <div className="flex flex-col gap-1">
              <Input
                placeholder="Invite by email"
                value={inviteEmail}
                onChange={e => { setInviteEmail(e.target.value); setEmailStatus(null); }}
                onBlur={e => checkEmail(e.target.value)}
                className="max-w-xs"
              />
              {checkingEmail && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Checking…
                </span>
              )}
              {emailStatus && !checkingEmail && (
                <span className={`text-xs ${
                  emailStatus.status === 'conflict' ? 'text-red-600'
                  : emailStatus.status === 'exists_in_org' ? 'text-blue-600'
                  : 'text-green-600'
                }`}>
                  {emailStatus.status === 'conflict' && '✗ Registered to another institution'}
                  {emailStatus.status === 'exists_in_org' && `Already a member as ${emailStatus.role_name}`}
                  {emailStatus.status === 'available' && '✓ Available'}
                </span>
              )}
            </div>
            <Select value={inviteRoleId} onValueChange={setInviteRoleId}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>
                {availableRoles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={sendInvite} disabled={emailStatus?.status === 'conflict'}>
              Send Invite
            </Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map(m => (
              <TableRow key={m.id}>
                <TableCell className="font-medium">{m.full_name}</TableCell>
                <TableCell>
                  {canManage ? (
                    <Select
                      value={m.role_id}
                      onValueChange={(v) => changeRole(m.id, v)}
                      disabled={!isAppAdmin && m.roles?.name === 'CCO'} // CCO row locked for non-platform-admins
                    >
                      <SelectTrigger className="w-44 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableRoles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <span>{m.roles?.name}</span>}
                </TableCell>
                <TableCell><Badge variant={m.status === "active" ? "default" : "secondary"}>{m.status}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{new Date(m.joined_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  {canManage && (
                    m.status === "active"
                      ? <Button size="sm" variant="ghost" onClick={() => setStatus(m.id, "suspended")}>Suspend</Button>
                      : <Button size="sm" variant="ghost" onClick={() => setStatus(m.id, "active")}>Reactivate</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
